import React, { useState } from 'react';
import { Calendar, Clock, Search, X, Trash2, Edit2, Save, Copy, ExternalLink } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';

const ScheduleCallTemplate = () => {
  const { darkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState(null);
  
  const [templates, setTemplates] = useState([
    {
      id: 1,
      title: "1 on 1 Meetings",
      description: "Schedule one-on-one meetings with team members and stakeholders.",
      departments: {
        sales: "https://calendly.com/example/sales-1on1",
        operations: "https://calendly.com/example/operations-1on1",
        technology: "https://calendly.com/example/tech-1on1",
        hr: "https://calendly.com/example/hr-1on1",
        finance: "https://calendly.com/example/finance-1on1",
        marketing: "https://calendly.com/example/marketing-1on1"
      },
      duration: "30 min",
      location: "Zoom",
      type: "General",
      hosts: ["John Smith", "Sarah Johnson"]
    }
  ]);

  const departments = [
    { id: 'all', name: 'All Departments', color: 'bg-gray-500' },
    { id: 'sales', name: 'Sales', color: 'bg-red-500' },
    { id: 'operations', name: 'Operations', color: 'bg-blue-500' },
    { id: 'technology', name: 'Technology', color: 'bg-purple-500' },
    { id: 'hr', name: 'HR', color: 'bg-yellow-500' },
    { id: 'finance', name: 'Finance', color: 'bg-green-500' },
    { id: 'marketing', name: 'Marketing', color: 'bg-orange-500' }
  ];

  const typeOptions = ["General", "Lead", "Client", "Internal"];

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = selectedDepartment === 'all' || template.departments[selectedDepartment];
    return matchesSearch && matchesDepartment;
  });

  const handleEdit = () => {
    setEditedTemplate({ ...selectedTemplate });
    setIsEditing(true);
  };

  const handleSave = () => {
    setTemplates(prevTemplates =>
      prevTemplates.map(template =>
        template.id === editedTemplate.id ? editedTemplate : template
      )
    );
    setSelectedTemplate(editedTemplate);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      setTemplates(prevTemplates => 
        prevTemplates.filter(template => template.id !== selectedTemplate.id)
      );
      setSelectedTemplate(null);
      setEditedTemplate(null);
      setIsEditing(false);
    }
  };



  const handleClose = () => {
    setSelectedTemplate(null);
    setEditedTemplate(null);
    setIsEditing(false);
  };

  const handleCopyLink = (e, link) => {
    e.stopPropagation();
    if (link) {
      navigator.clipboard.writeText(link);
      // You could add a toast notification here
      alert('Link copied to clipboard!');
    }
  };

  const handleOpenLink = (e, link) => {
    e.stopPropagation();
    if (link) {
      window.open(link, '_blank');
    }
  };

  const locationOptions = ["Zoom", "Google Meet", "Phone Call", "In Person"];

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <BackButton to="/crm/templates" />
          <div className="flex-1 text-center">
            <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>
              Schedule Call Templates
            </h1>
          </div>
          <div className="w-10"></div> {/* Spacer to balance the back button */}
        </div>

        {/* Search and Filter Bar */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 mb-6`}>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                  darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900'
                }`}
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Department Filter */}
            <div className="w-full md:w-48">
              <select
                className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                  darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900'
                }`}
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>


          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map(template => (
            <div
              key={template.id}
              onClick={() => setSelectedTemplate(template)}
              className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer`}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {template.title}
                </h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  template.type === 'General' ? 'bg-gray-100 text-gray-800' :
                  template.type === 'Lead' ? 'bg-blue-100 text-blue-800' :
                  template.type === 'Client' ? 'bg-green-100 text-green-800' :
                  'bg-purple-100 text-purple-800'
                }`}>
                  {template.type}
                </span>
              </div>
              
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {template.description}
              </p>

              <div className="space-y-2 mb-4">
                {Object.entries(template.departments).map(([dept, link]) => {
                  const deptInfo = departments.find(d => d.id === dept);
                  if (!deptInfo || deptInfo.id === 'all') return null;
                  
                  return (
                    <div key={dept} className="flex items-center justify-between">
                      <span className={`text-xs px-2 py-1 rounded ${deptInfo.color} text-white`}>
                        {deptInfo.name}
                      </span>
                      <div className="flex space-x-1">
                        {link && (
                          <>
                            <button
                              onClick={(e) => handleCopyLink(e, link)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                              title="Copy link"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                            <button
                              onClick={(e) => handleOpenLink(e, link)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                              title="Open link"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>{template.duration}</span>
                </div>
                <span>{template.location}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredTemplates.length === 0 && (
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8 text-center`}>
            <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              No templates found
            </h3>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {searchQuery || selectedDepartment !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first scheduling template.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Template Details Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden border`}>
            {/* Modal Header */}
            <div className="bg-red-600 p-4 flex justify-between items-center">
              {isEditing ? (
                <input
                  type="text"
                  className="flex-1 px-3 py-2 bg-white rounded-md text-gray-900 font-bold text-xl mr-4"
                  value={editedTemplate.title}
                  onChange={(e) => setEditedTemplate({ ...editedTemplate, title: e.target.value })}
                />
              ) : (
                <h2 className="text-xl font-bold text-white">{selectedTemplate.title}</h2>
              )}
              <button
                onClick={handleClose}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className={`p-6 overflow-y-auto max-h-[calc(90vh-120px)] ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              {isEditing ? (
                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Description
                    </label>
                    <textarea
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900'
                      }`}
                      rows="3"
                      value={editedTemplate.description}
                      onChange={(e) => setEditedTemplate({ ...editedTemplate, description: e.target.value })}
                    />
                  </div>

                  {/* Department Links */}
                  <div>
                    <label className={`block text-sm font-medium mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Department Scheduling Links
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {departments.filter(dept => dept.id !== 'all').map(dept => (
                        <div key={dept.id}>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {dept.name}
                          </label>
                          <input
                            type="url"
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                              darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900'
                            }`}
                            placeholder="Enter scheduling link..."
                            value={editedTemplate.departments[dept.id] || ''}
                            onChange={(e) => setEditedTemplate({
                              ...editedTemplate,
                              departments: {
                                ...editedTemplate.departments,
                                [dept.id]: e.target.value
                              }
                            })}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Duration and Location */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Duration
                      </label>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                          darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900'
                        }`}
                        value={editedTemplate.duration}
                        onChange={(e) => setEditedTemplate({ ...editedTemplate, duration: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Location
                      </label>
                      <select
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                          darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900'
                        }`}
                        value={editedTemplate.location}
                        onChange={(e) => setEditedTemplate({ ...editedTemplate, location: e.target.value })}
                      >
                        {locationOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Type */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Type
                    </label>
                    <select
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900'
                      }`}
                      value={editedTemplate.type}
                      onChange={(e) => setEditedTemplate({ ...editedTemplate, type: e.target.value })}
                    >
                      {typeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <h3 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Description
                    </h3>
                    <p className={`${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedTemplate.description}
                    </p>
                  </div>

                  {/* Department Links */}
                  <div>
                    <h3 className={`text-sm font-medium mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Department Scheduling Links
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {departments.filter(dept => dept.id !== 'all').map(dept => {
                        const link = selectedTemplate.departments[dept.id];
                        return (
                          <div key={dept.id} className={`p-3 border rounded-xl border-gray-200 dark:border-gray-700`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-xs px-2 py-1 rounded ${dept.color} text-white`}>
                                {dept.name}
                              </span>
                              {link && (
                                <div className="flex space-x-1">
                                  <button
                                    onClick={() => handleCopyLink(null, link)}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                    title="Copy link"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => handleOpenLink(null, link)}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                    title="Open link"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                            {link ? (
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`text-sm text-blue-600 hover:text-blue-800 break-all ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}
                              >
                                {link}
                              </a>
                            ) : (
                              <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                No link provided
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <h3 className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Duration
                      </h3>
                      <p className={`${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {selectedTemplate.duration}
                      </p>
                    </div>
                    <div>
                      <h3 className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Location
                      </h3>
                      <p className={`${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {selectedTemplate.location}
                      </p>
                    </div>
                    <div>
                      <h3 className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Type
                      </h3>
                      <p className={`${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {selectedTemplate.type}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className={`flex justify-end space-x-3 mt-6 pt-6 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className={`px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium ${
                        darkMode 
                          ? 'text-gray-300 bg-gray-800 border-gray-600 hover:bg-gray-700' 
                          : 'text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    >
                      Save Changes
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleDelete}
                      className="px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                    >
                      Delete
                    </button>
                    <button
                      onClick={handleEdit}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    >
                      Edit
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleCallTemplate; 